import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { adminListBankAccounts, adminCreateBankAccount, adminUpdateBankAccount, adminDeleteBankAccount } from '../../api';
import { toast } from '../../hooks/use-toast';
import { Landmark, Plus, Pencil, Trash2, Star, Check } from 'lucide-react';

const empty = { label: '', bank_name: '', beneficiary: '', iban: '', bic: '', account_number: '', address: '', currency: 'AED', is_default: false };
const FIELDS = [
  ['label', 'Label (internal)', 'الاسم (داخلي)'], ['bank_name', 'Bank name', 'اسم البنك'], ['beneficiary', 'Account holder / Beneficiary', 'اسم صاحب الحساب'],
  ['iban', 'IBAN', 'IBAN'], ['account_number', 'Account number', 'رقم الحساب'], ['bic', 'BIC / SWIFT', 'BIC / SWIFT'], ['address', 'Bank address', 'عنوان البنك'],
];

export const BankAccountsManager = ({ open, onClose, lang, onChanged }) => {
  const ar = lang === 'ar';
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => adminListBankAccounts().then(setList).catch(() => {});
  useEffect(() => { if (open) { load(); setForm(null); } }, [open]);

  const save = async () => {
    if (!form.label || !form.bank_name || !form.beneficiary || !form.iban) { toast({ title: ar ? 'الاسم، البنك، صاحب الحساب وIBAN مطلوبة' : 'Label, bank, beneficiary and IBAN are required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (form.id) await adminUpdateBankAccount(form.id, form); else await adminCreateBankAccount(form);
      toast({ title: ar ? 'تم الحفظ' : 'Saved' }); setForm(null); await load(); onChanged?.();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); } finally { setSaving(false); }
  };
  const makeDefault = async (a) => { await adminUpdateBankAccount(a.id, { is_default: true }); await load(); onChanged?.(); };
  const remove = async (a) => {
    if (!window.confirm(ar ? `حذف ${a.label}؟` : `Delete ${a.label}?`)) return;
    try { await adminDeleteBankAccount(a.id); await load(); onChanged?.(); } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="bank-accounts-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Landmark className="w-5 h-5 text-[#2563eb]" /> {ar ? 'الحسابات البنكية للفواتير' : 'Bank accounts for receipts'}</DialogTitle></DialogHeader>
        {!form ? (
          <>
            <ul className="space-y-2 mt-2">
              {list.map((a) => (
                <li key={a.id} className={`rounded-xl border p-3 flex items-start gap-3 ${a.is_default ? 'border-[#2563eb] bg-[#2563eb]/5' : 'border-[#e5e7eb]'}`} data-testid={`bank-acc-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#0a0a0a]">{a.label}{a.is_default && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#2563eb] text-white"><Star className="w-3 h-3" /> {ar ? 'افتراضي' : 'Default'}</span>}</div>
                    <div className="text-xs text-[#111827]/70 mt-0.5">{a.beneficiary} · {a.bank_name}</div>
                    <div className="text-xs font-mono text-[#111827]/80">{a.iban}{a.bic ? ` · ${a.bic}` : ''}</div>
                  </div>
                  {!a.is_default && <button onClick={() => makeDefault(a)} className="p-1.5 rounded hover:bg-[#f8fafc] text-[#2563eb]" title={ar ? 'تعيين كافتراضي' : 'Set default'} data-testid={`bank-acc-default-${a.id}`}><Check className="w-4 h-4" /></button>}
                  <button onClick={() => setForm({ ...empty, ...a })} className="p-1.5 rounded hover:bg-[#f8fafc] text-[#111827]/70" data-testid={`bank-acc-edit-${a.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(a)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`bank-acc-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
            <Button onClick={() => setForm({ ...empty })} className="gold-button rounded-md h-10 px-4 text-sm mt-3" data-testid="bank-acc-new-btn"><Plus className="w-4 h-4 me-1" /> {ar ? 'إضافة حساب بنكي' : 'Add bank account'}</Button>
          </>
        ) : (
          <div className="mt-2 space-y-3" data-testid="bank-acc-form">
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, en, arL]) => (
                <div key={k} className={k === 'address' || k === 'beneficiary' ? 'sm:col-span-2' : ''}>
                  <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{ar ? arL : en}</label>
                  <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`bg-[#f8fafc] border-[#2563eb]/25 text-[#111827] ${k === 'iban' || k === 'account_number' || k === 'bic' ? 'font-mono' : ''}`} dir="ltr" data-testid={`bank-acc-input-${k}`} />
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-[#111827]"><input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="bank-acc-input-default" /> {ar ? 'استخدامه كحساب افتراضي في الفواتير الجديدة' : 'Use as default on new receipts'}</label>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setForm(null)} className="ghost-button bg-transparent h-10 px-4 text-sm rounded-md">{ar ? 'إلغاء' : 'Cancel'}</Button>
              <Button onClick={save} disabled={saving} className="gold-button h-10 px-6 text-sm rounded-md" data-testid="bank-acc-save-btn">{saving ? '...' : ar ? 'حفظ' : 'Save'}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
